// src/pages/UsersPage.jsx
import React, { useEffect, useState } from "react";
import api from "../api/client";           // ✅ use default import
import "../styles/UsersPage.css";

export default function UsersPage() {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [err, setErr] = useState("");

  const [form, setForm] = useState({
    id: null,
    username: "",
    email: "",
    password: "",
    role_id: "", // single role FK
  });

  const isEdit = !!form.id;

  const fetchRoles = async () => {
    try {
      // ✅ add leading slash so final URL is /api/roles/
      const { data } = await api.get("/roles/");
      setRoles(data || []);
    } catch (e) {
      console.error("fetchRoles error", e);
      setErr(e?.response?.data?.detail || "Failed to load roles");
    }
  };

  const fetchUsers = async () => {
    try {
      // ✅ /api/users/
      const { data } = await api.get("/users/");
      setUsers(data || []);
    } catch (e) {
      console.error("fetchUsers error", e);
      setErr(e?.response?.data?.detail || "Failed to load users");
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  const resetForm = () =>
    setForm({
      id: null,
      username: "",
      email: "",
      password: "",
      role_id: "",
    });

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Single-select checkbox behavior: selecting one unchecks others
  const toggleRole = (roleId) => {
    setForm((f) => ({
      ...f,
      role_id: String(f.role_id) === String(roleId) ? "" : String(roleId),
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");

    try {
      const payload = {
        username: form.username,
        email: form.email,
        password: form.password || "Temp123!@#", // backend can hash/ignore for SSO
        role_id: Number(form.role_id),
      };

      if (isEdit) {
        // ✅ /api/users/{id}
        await api.put(`/users/${form.id}`, payload);
      } else {
        await api.post("/users/", payload);
      }

      resetForm();
      setEditingUser(null);
      fetchUsers();
    } catch (e2) {
      console.error("onSubmit error", e2);
      setErr(e2?.response?.data?.detail || "Save failed");
    }
  };

  const onEdit = (u) => {
    setEditingUser(u);
    setForm({
      id: u.id,
      username: u.username,
      email: u.email,
      password: "",
      role_id: u.role?.id || "",
    });
  };

  const onDelete = async (id) => {
    if (!window.confirm("Deactivate this user?")) return;
    try {
      // ✅ /api/users/{id}  (soft delete → sets is_active=false)
      await api.delete(`/users/${id}`);
      fetchUsers();
    } catch (e) {
      console.error("onDelete error", e);
      setErr(e?.response?.data?.detail || "Deactivate failed");
    }
  };

  const onActivate = async (id) => {
    if (!window.confirm("Reactivate this user?")) return;
    try {
      await api.put(`/users/${id}/activate`);
      fetchUsers();
    } catch (e) {
      console.error("onActivate error", e);
      setErr(e?.response?.data?.detail || "Activate failed");
    }
  };

  return (
    <div className="users-page">
      <h2>{isEdit ? "Edit User" : "Create User"}</h2>

      <form className="user-form" onSubmit={onSubmit}>
        <div className="full">
          <label className="label" htmlFor="username">
            Username
          </label>
          <input
            id="username"
            name="username"
            className="input"
            placeholder="jane.doe"
            value={form.username}
            onChange={onChange}
            required
          />
        </div>

        <div className="full">
          <label className="label" htmlFor="email">
            Email
          </label>
          <input
            id="email"
            name="email"
            className="input"
            type="email"
            placeholder="jane@company.com"
            value={form.email}
            onChange={onChange}
            required
          />
        </div>

        <div className="full">
          <label className="label" htmlFor="password">
            {isEdit ? "New Password (optional)" : "Password"}
          </label>
          <input
            id="password"
            name="password"
            className="input"
            type="password"
            placeholder={
              isEdit ? "Leave blank to keep current" : "Set a strong password"
            }
            value={form.password}
            onChange={onChange}
            required={!isEdit}
          />
        </div>

        {/* Roles checkbox grid (single selection) */}
        <div className="full">
          <label className="label">Role</label>
          <div className="role-box">
            <div className="role-grid">
              {roles.map((r) => {
                const checked = String(form.role_id) === String(r.id);
                return (
                  <label key={r.id} className="role-item">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleRole(r.id)}
                    />
                    <span>{r.name}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>

        <div className="btn-row full">
          <button type="submit" className="btn primary">
            {isEdit ? "Update User" : "Create User"}
          </button>
          {isEdit && (
            <button
              type="button"
              className="btn muted"
              onClick={() => {
                resetForm();
                setEditingUser(null);
              }}
            >
              Cancel
            </button>
          )}
        </div>

        {err && <div className="error full">{err}</div>}
      </form>

      <div className="section-title">User List</div>
      <div className="table-wrap">
        <table className="users-table" role="table">
          <thead>
            <tr>
              <th style={{ width: 60 }}>ID</th>
              <th>Username</th>
              <th>Email</th>
              <th style={{ width: 180 }}>Role</th>
              <th style={{ width: 120 }}>Status</th>
              <th style={{ width: 200 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id}>
                <td>{u.id}</td>
                <td>{u.username}</td>
                <td>{u.email}</td>
                <td>{u.role?.name || "—"}</td>
                <td>
                  <span className={`badge ${u.is_active ? "ok" : "muted"}`}>
                    {u.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td>
                  <div className="actions">
                    <button className="btn muted" onClick={() => onEdit(u)}>
                      Edit
                    </button>
                    {u.is_active ? (
                      <button
                        className="btn danger"
                        onClick={() => onDelete(u.id)}
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        className="btn success"
                        onClick={() => onActivate(u.id)}
                      >
                        Activate
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td
                  colSpan={6}
                  style={{
                    color: "var(--muted)",
                    fontStyle: "italic",
                    textAlign: "center",
                  }}
                >
                  No users yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
